import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { find, includes, prop, propEq } from "ramda";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TablePagination from "@material-ui/core/TablePagination";

import EditIcon from "@material-ui/icons/Edit";
import { notBlank } from "../util/ext";
import { useMediaTable } from "../creator-application/common/use-mediaTable";
import Tooltip from "@material-ui/core/Tooltip";
import { Box } from "@material-ui/core";
import AuthenticationDetailsCell from "./AuthenticationDetailsCell";
import { authTypes } from "./authenticationService";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },

  cell: {
    minWidth: 100,
    maxWidth: 400,
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const AuthenticationTable = props => {
  const {
    authenticationList,
    page,
    onPageChange,
    total = 0,
    onSelectChange,
    orderBy = "organizationName",
    order = "asc",
    onSortChange,
    onScrollVisible,
    onDeleteClicked,
    onEditClicked,
  } = props;

  const history = useHistory();
  const classes = useStyles();
  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Authentications">
          <TableHead>
            <TableRow>
              <TableCell
                key="organization"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "organizationName"}
                  direction={order}
                  onClick={() => handleRequestSort("organizationName")}
                >
                  Organization
                  {orderBy === "organizationName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="alias" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "organizationAlias"}
                  direction={order}
                  onClick={() => handleRequestSort("organizationAlias")}
                >
                  Alias
                  {orderBy === "organizationAlias" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="type" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "type"}
                  direction={order}
                  onClick={() => handleRequestSort("type")}
                >
                  Type
                  {orderBy === "type" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="date-updated"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "dateUpdated"}
                  direction={order}
                  onClick={() => handleRequestSort("dateUpdated")}
                >
                  Last Update
                  {orderBy === "dateUpdated" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="updated-by" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "updatedBy"}
                  direction={order}
                  onClick={() => handleRequestSort("type")}
                >
                  Updated By
                  {orderBy === "updatedBy" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="details" align="left" className={classes.cell}>
                Details
              </TableCell>

              <TableCell key="actions" align="center" style={{ width: 100 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {authenticationList.map(auth => (
              <TableRow
                onClick={() => handleRowClick(auth)}
                hover
                tabIndex={-1}
                key={auth.authenticationId}
                selected={includes(auth.authenticationId, state.selected)}
              >
                <TableCell
                  key={"organization-name:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  <Button
                    onClick={() =>
                      history.push(
                        `/manage-organizations/organizations/${auth.organizationId}`,
                        {
                          label: "Back to Manage Authentication",
                          previousLocation: "/manage-authentication",
                        },
                      )
                    }
                    aria-label={`View ${auth.organizationName}`}
                  >
                    {auth.organizationName}
                  </Button>
                </TableCell>

                <TableCell
                  key={"organization-alias:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {auth.organizationAlias}
                </TableCell>

                <TableCell
                  key={"type:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {prop("display", find(propEq("value", auth.type), authTypes))}
                </TableCell>

                <TableCell
                  key={"date-updated:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {new Date(auth.dateUpdated).toLocaleDateString()}
                </TableCell>

                <TableCell
                  key={"updated-by:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  {auth.updatedByUsername}
                </TableCell>

                <TableCell
                  key={"details:" + auth.authenticationId}
                  align="left"
                  className={classes.cell}
                >
                  <AuthenticationDetailsCell authentication={auth} />
                </TableCell>

                <TableCell
                  key={"actions:" + auth.authenticationId}
                  align="center"
                  style={{ width: 100 }}
                >
                  <Box display="flex" justifyContent="space-between">
                    {(auth.type === "sip" ||
                      auth.type === "papi" ||
                      auth.type === "barcode") && (
                      <Tooltip title="Edit">
                        <IconButton
                          classes={{ label: classes.icon }}
                          onClick={() => onEditClicked(auth)}
                        >
                          <EditIcon fontSize="large" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <ConfirmButton
                      dialogTitle="Delete Authentication"
                      dialogBody="Are you sure you want to delete this authentication?"
                      handleDelete={() => onDeleteClicked(auth)}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[10]}
        component="div"
        count={total}
        rowsPerPage={10}
        page={page}
        onChangePage={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default AuthenticationTable;
